import React from 'react';
import { Link } from 'react-router-dom';

const companyInfo = {
  mainCompany: 'GTB Holdings株式会社',
  branch: 'LUAN LOGISTICS',
  representative: 'Shinpei Ota (太田 真平)',
  address: 'Kohara Building 307, 1-11-10 Uchikanda, Chiyoda-ku, Tokyo 101-0047, Japan',
  phone: '03-6281-7035',
  email: 'luanlogistics@onebusiness.jp',
};

const contactCards = [
  { label: 'Seller / Business Operator', value: companyInfo.mainCompany },
  { label: 'Brand / Business Name', value: companyInfo.branch },
  { label: 'Representative', value: companyInfo.representative },
  { label: 'Address', value: companyInfo.address },
  { label: 'Phone', value: companyInfo.phone },
  { label: 'Email', value: companyInfo.email },
];

function InfoPage({ title, intro, sections, ctaText }) {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-header">
          <span className="hero-badge">LUAN LOGISTICS</span>
          <h1>{title}</h1>
          {intro && <p>{intro}</p>}
        </div>

        <div className="legal-content">
          {sections.map((section) => (
            <section key={section.title} className="legal-section">
              <h2>{section.title}</h2>
              {section.body &&
                section.body.map((paragraph, index) => (
                  <p key={`${section.title}-${index}`}>{paragraph}</p>
                ))}
              {section.list && (
                <ul className="legal-list">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {ctaText && (
            <div className="legal-cta">
              <p>{ctaText}</p>
              <div className="cta-actions">
                <Link to="/shop" className="btn btn-primary btn-lg">Browse Products</Link>
                <Link to="/contact-us" className="btn btn-outline btn-lg">Contact Us</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommercialTransactionInformationPage() {
  return (
    <InfoPage
      title="Commercial Transaction Information"
      intro="This page contains the key commercial transaction details for LUAN LOGISTICS and its business operations."
      sections={[
        {
          title: 'Business Information',
          body: [
            'Seller / Business Operator: GTB Holdings株式会社',
            'Brand / Business Name: LUAN LOGISTICS',
            'Representative: Shinpei Ota (太田 真平)',
            'Business Address: Kohara Building 307, 1-11-10 Uchikanda, Chiyoda-ku, Tokyo 101-0047, Japan',
            'Phone Number: 03-6281-7035',
            'Email Address: luanlogistics@onebusiness.jp',
          ],
        },
        {
          title: 'Pricing, Shipping, and Payment',
          body: [
            'All product prices are displayed on the relevant product pages.',
            'All displayed prices include Japanese Consumption Tax unless otherwise stated.',
            'Standard shipping fees are covered by the company.',
          ],
          list: [
            'Credit Card',
            'Bank Transfer',
            'Cash on Delivery (COD)',
            'Deferred Payment, where available',
          ],
        },
        {
          title: 'Payment Timing and Delivery',
          body: [
            'Payment timing depends on the payment method selected by the customer.',
            'Orders are normally shipped within 1–3 business days after the order and required payment procedures have been confirmed.',
          ],
        },
        {
          title: 'Returns and Cancellations',
          body: [
            'Customers may request a return within 7 days after receiving the product, subject to the Return & Refund Policy.',
            'Products must be unopened, unused, and in their original condition.',
            'Opened or used products cannot be returned.',
            'LUAN LOGISTICS will cover the return shipping costs for eligible returns.',
            'Refunds will be processed after the returned product has been received and inspected.',
            'Customers should contact LUAN LOGISTICS as soon as possible if they wish to cancel an order.',
            'Cancellation may not be possible after the order has been shipped.',
          ],
        },
      ]}
      ctaText="Need help before placing an order? Our team can explain payment, delivery, and return details."
    />
  );
}

export function ReturnRefundPolicyPage() {
  return (
    <InfoPage
      title="Return & Refund Policy"
      intro="At LUAN LOGISTICS, we are committed to providing our customers with a safe and reliable shopping experience."
      sections={[
        {
          title: 'Return Eligibility',
          body: [
            'Customers may request a return within 7 days after receiving their order, subject to the conditions below.',
          ],
          list: [
            'The product is unused and unopened.',
            'The product is in its original condition.',
            'The customer contacts us within 7 days of receiving the order.',
          ],
        },
        {
          title: 'Non-Returnable Products',
          list: [
            'The product has been opened.',
            'The product has been used.',
            'The product has been damaged or altered by the customer.',
          ],
        },
        {
          title: 'Customer Change of Mind',
          body: [
            'Customers may request a return if they change their mind about the purchase, provided that the product is unopened, unused, and in its original condition.',
          ],
        },
        {
          title: 'Return Shipping Costs',
          body: [
            'LUAN LOGISTICS will cover the return shipping costs for eligible returns.',
          ],
        },
        {
          title: 'Refund Process',
          body: [
            'After the returned product has been received and inspected, LUAN LOGISTICS will process the refund.',
            'The refund will normally be made using the original payment method.',
            'The actual processing time may vary depending on the payment method and financial institution.',
          ],
        },
        {
          title: 'How to Request a Return or Refund',
          body: [
            'Customers should contact LUAN LOGISTICS and provide:',
          ],
          list: [
            'Order number',
            'Customer name',
            'Product name',
            'Reason for the return',
          ],
        },
        {
          title: 'Contact',
          body: [
            'LUAN LOGISTICS',
            'Email: luanlogistics@onebusiness.jp',
            'Phone: 03-6281-7035',
          ],
        },
      ]}
      ctaText="If you need to return an item, contact us as soon as possible and include your order details."
    />
  );
}

export function PaymentMethodsPage() {
  return (
    <InfoPage
      title="Payment Methods"
      intro="LUAN LOGISTICS provides a range of payment options to support safe and convenient checkout."
      sections={[
        {
          title: 'Available Payment Methods',
          list: [
            'Credit Card: Customers can pay by credit card before the order is processed.',
            'Bank Transfer: Customers can make payment by bank transfer before the order is processed.',
            'Cash on Delivery (COD): Customers may pay for their order upon delivery when COD is available for the selected order and delivery method.',
            'Deferred Payment: Customers may use deferred payment where this payment method is available.',
          ],
        },
        {
          title: 'Order Details',
          body: [
            'The payment method available for each order will be clearly displayed during the ordering process.',
            'All prices displayed on our website include Japanese Consumption Tax unless otherwise stated.',
            'Standard shipping fees are covered by the company.',
          ],
        },
      ]}
      ctaText="Choose the option that best fits your order at checkout."
    />
  );
}

export function PrivacyPolicyPage() {
  return (
    <InfoPage
      title="Privacy Policy"
      intro="GTB Holdings株式会社 and its LUAN LOGISTICS brand respect the privacy of our customers and handle personal information appropriately."
      sections={[
        {
          title: 'Information We Collect',
          list: [
            'Name',
            'Address',
            'Phone number',
            'Email address',
            'Shipping information',
            'Order information',
            'Payment-related information',
          ],
        },
        {
          title: 'How We Use Information',
          list: [
            'Processing and delivering orders',
            'Processing payments',
            'Customer support',
            'Handling returns and refunds',
            'Communicating with customers regarding their orders',
            'Improving our services',
          ],
        },
        {
          title: 'Security',
          body: [
            'We take reasonable measures to protect customer information from unauthorized access, loss, alteration, or disclosure.',
          ],
        },
        {
          title: 'Inquiries',
          body: [
            'For inquiries regarding personal information, please contact:',
            'GTB Holdings株式会社 / LUAN LOGISTICS',
            'Email: luanlogistics@onebusiness.jp',
            'Phone: 03-6281-7035',
          ],
        },
      ]}
      ctaText="Your privacy matters to us. Contact our team if you have questions about your information."
    />
  );
}

export function TermsConditionsPage() {
  return (
    <InfoPage
      title="Terms & Conditions"
      intro="By using the LUAN LOGISTICS website and placing an order, customers agree to these Terms & Conditions."
      sections={[
        {
          title: 'General Terms',
          body: [
            'Product prices, product descriptions, availability, payment methods, and delivery information are displayed on the website.',
            'Customers are responsible for providing accurate information when placing an order.',
            'An order may be cancelled if the product is unavailable, the information provided by the customer is incorrect, or other circumstances prevent the order from being fulfilled.',
            'Returns and refunds are handled according to the LUAN LOGISTICS Return & Refund Policy.',
            'LUAN LOGISTICS reserves the right to update these Terms & Conditions when necessary.',
            'Updated terms will be published on this website.',
          ],
        },
        {
          title: 'Questions',
          body: [
            'For questions regarding these Terms & Conditions, please contact:',
            'GTB Holdings株式会社 / LUAN LOGISTICS',
            'Email: luanlogistics@onebusiness.jp',
            'Phone: 03-6281-7035',
          ],
        },
      ]}
      ctaText="Please review the policies before completing checkout."
    />
  );
}

export function ContactUsPage() {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-header">
          <span className="hero-badge">Contact</span>
          <h1>Contact Us</h1>
          <p>We are here to help with orders, returns, payments, and general inquiries.</p>
        </div>

        <div className="contact-grid">
          <div className="legal-section info-card">
            <h2>Company Information</h2>
            <ul className="legal-list">
              {contactCards.map((item) => (
                <li key={item.label}>
                  <strong>{item.label}:</strong> {item.value}
                </li>
              ))}
            </ul>
          </div>

          <div className="legal-section info-card">
            <h2>Get in Touch</h2>
            <div className="contact-actions">
              <a href="mailto:luanlogistics@onebusiness.jp" className="btn btn-primary btn-lg">Email Us</a>
              <a href="tel:0362817035" className="btn btn-outline btn-lg">Call Us</a>
            </div>
            <div className="contact-meta">
              <p><strong>Email:</strong> <a href="mailto:luanlogistics@onebusiness.jp">luanlogistics@onebusiness.jp</a></p>
              <p><strong>Phone:</strong> <a href="tel:0362817035">03-6281-7035</a></p>
              <p><strong>Address:</strong> Kohara Building 307, 1-11-10 Uchikanda, Chiyoda-ku, Tokyo 101-0047, Japan</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { companyInfo };
